
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, MessageCircle, ArrowRight, Clock } from 'lucide-react';

interface PlanLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCount: number;
  limit: number;
  planType: string;
}

const PlanLimitModal: React.FC<PlanLimitModalProps> = ({
  isOpen,
  onClose,
  currentCount,
  limit,
  planType
}) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate('/main/subscriptions');
    onClose();
  };

  // Calcular horas até o reset (meia-noite)
  const calculateHoursUntilReset = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const diffMs = tomorrow.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60));
  };

  const hoursUntilReset = calculateHoursUntilReset();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gradient-to-br from-gray-900 to-black border-white/20 text-white max-w-md">
        <DialogHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-luxury-red/20 rounded-full flex items-center justify-center">
            <MessageCircle className="h-8 w-8 text-luxury-red" />
          </div>
          
          <DialogTitle className="text-2xl font-bold text-white">
            Limite Diário Atingido!
          </DialogTitle>
          
          <DialogDescription className="text-gray-300 space-y-3">
            <div className="flex items-center justify-center gap-2">
              <Badge variant="outline" className="border-luxury-red text-luxury-red">
                {planType === 'sponsor_free' ? 'Sponsor Free' : planType}
              </Badge>
            </div>
            
            <p>
              Você já enviou <strong>{currentCount} de {limit} mensagens</strong> hoje.
            </p>
            
            <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
              <Clock className="h-4 w-4" />
              <span>
                Próximas mensagens disponíveis em {hoursUntilReset} hora{hoursUntilReset !== 1 ? 's' : ''}
              </span>
            </div>
            
            <p className="text-sm">
              Faça upgrade para um plano superior e desfrute de mensagens ilimitadas!
            </p>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-6">
          <Button 
            onClick={handleUpgrade}
            className="bg-gradient-to-r from-luxury-gold to-yellow-500 hover:from-luxury-gold/90 hover:to-yellow-500/90 text-black font-semibold"
          >
            <Crown className="h-4 w-4 mr-2" />
            Upgrade para Premium
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-white/20 text-white hover:bg-white/10"
          >
            Entendi
          </Button>
        </div>

        <div className="mt-4 p-3 bg-white/5 rounded-lg text-center">
          <p className="text-xs text-gray-400">
            💎 Com planos superiores você também ganha filtros avançados, suporte prioritário e muito mais!
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanLimitModal;
